/**
 * User Service
 */
import { getApiClient } from '../apiClient';
import { USER_ENDPOINTS, replacePathParams } from '../endpoints';
import type {
  GetUsersResponse,
  GetUserResponse,
  CreateUserPayload,
  CreateUserResponse,
  UpdateUserPayload,
  UpdateUserResponse,
  DeleteUserResponse,
  GetUsersParams,
  ExportUsersResponse,
  GetCommunityActivityResponse,
  GetAchievementsResponse,
  GetTransactionsResponse,
  GetLoginHistoryResponse,
  GetUserActivityResponse,
} from '../types/user.types';

export const userService = {
  /**
   * Get all users with pagination and search
   */
  getUsers: (params?: GetUsersParams): Promise<GetUsersResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.page) {
      queryParams.append('page', String(params.page));
    }
    if (params?.limit) {
      queryParams.append('limit', String(params.limit));
    }
    if (params?.offset) {
      queryParams.append('offset', String(params.offset));
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.isBlocked !== undefined) {
      queryParams.append('isBlocked', String(params.isBlocked));
    }
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    const url = queryParams.toString()
      ? `${USER_ENDPOINTS.getUsers}?${queryParams.toString()}`
      : USER_ENDPOINTS.getUsers;
    return getApiClient().get(url);
  },

  /**
   * Get single user by ID
   */
  getUser: (id: string): Promise<GetUserResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.getUser, { id });
    return getApiClient().get(url);
  },

  /**
   * Create new user
   */
  createUser: (body: CreateUserPayload): Promise<CreateUserResponse> => {
    return getApiClient().post(USER_ENDPOINTS.createUser, body);
  },

  /**
   * Update user
   */
  updateUser: (id: string, body: UpdateUserPayload): Promise<UpdateUserResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.updateUser, { id });
    return getApiClient().put(url, body);
  },

  /**
   * Delete user
   */
  deleteUser: (id: string): Promise<DeleteUserResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.deleteUser, { id });
    return getApiClient().delete(url);
  },

  /**
   * Export users to CSV
   */
  exportUsers: async (params?: GetUsersParams): Promise<ExportUsersResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.isBlocked !== undefined) {
      queryParams.append('isBlocked', String(params.isBlocked));
    }
    if (params?.startDate) {
      queryParams.append('startDate', params.startDate);
    }
    if (params?.endDate) {
      queryParams.append('endDate', params.endDate);
    }
    const url = queryParams.toString()
      ? `${USER_ENDPOINTS.exportUsers}?${queryParams.toString()}`
      : USER_ENDPOINTS.exportUsers;

    const client = getApiClient().getInstance();
    const response = await client.get(url, {
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  },

  /**
   * Get community activity for a user
   */
  getCommunityActivity: (id: string, search?: string): Promise<GetCommunityActivityResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.getCommunityActivity, { id });
    const fullUrl = search ? `${url}?search=${encodeURIComponent(search)}` : url;
    return getApiClient().get(fullUrl);
  },

  /**
   * Get achievements for a user
   */
  getAchievements: (id: string): Promise<GetAchievementsResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.getAchievements, { id });
    return getApiClient().get(url);
  },

  /**
   * Get transaction history for a user
   */
  getTransactions: (id: string): Promise<GetTransactionsResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.getTransactions, { id });
    return getApiClient().get(url);
  },

  /**
   * Get login history for a user
   */
  getLoginHistory: (id: string): Promise<GetLoginHistoryResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.getLoginHistory, { id });
    return getApiClient().get(url);
  },

  /**
   * Get user activity
   */
  getUserActivity: (userId: string): Promise<GetUserActivityResponse> => {
    const url = replacePathParams(USER_ENDPOINTS.getUserActivity, { id: userId });
    return getApiClient().get(url);
  },
};

